import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";

// JWT token interface
interface JwtPayload {
    sub: string;
    email: string;
    token_use: string;
    exp: number;
}

// Default expiration time (in minutes)
const DEFAULT_EXPIRATION = 60; // 1 hour

/**
 * Save tokens to both localStorage and cookies
 * @param accessToken The access token to save
 * @param refreshToken The refresh token to save
 * @param expirationMinutes Number of minutes until expiration
 */
export const saveTokens = (
    accessToken: string,
    refreshToken: string,
    expirationMinutes: number = DEFAULT_EXPIRATION,
) => {
    // Save to localStorage (used by existing code)
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);

    // Calculate expiration timestamp
    const expires = new Date();
    expires.setMinutes(expires.getMinutes() + expirationMinutes);

    // Save to cookies with expiration
    Cookies.set("accessToken", accessToken, { expires, secure: true, sameSite: "strict" });
    Cookies.set("refreshToken", refreshToken, { expires: 7, secure: true, sameSite: "strict" }); // Refresh token valid for 7 days

    // Also save expiration time
    localStorage.setItem("tokenExpiration", expires.toISOString());
};

/**
 * Clear all authentication tokens from storage
 */
export const clearTokens = () => {
    // Clear from localStorage
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("tokenExpiration");

    // Clear from cookies
    Cookies.remove("accessToken");
    Cookies.remove("refreshToken");
};

/**
 * Check if the current access token is valid and not expired
 * @returns True if token is valid, false otherwise
 */
export const isTokenValid = (): boolean => {
    try {
        // Try to get token from cookie first, then localStorage as fallback
        const token = Cookies.get("accessToken") || localStorage.getItem("accessToken");
        if (!token) {
            // console.log("No token found in cookies or localStorage");
            return false;
        }
        // Decode token to check expiration
        const decoded = jwtDecode<JwtPayload>(token);
        // Get current time in seconds
        const currentTime = Math.floor(Date.now() / 1000);
        // Check if token is expired (with 10-second buffer)
        const isValid = decoded.exp > currentTime + 10;
        if (!isValid) {
            // console.log("Token is expired or about to expire");
        }
        return isValid;
    } catch (error) {
        console.error("Error validating token:", error);
        // If there's an error (like invalid token format), clear the tokens
        clearTokens();
        return false;
    }
};

/**
 * Get the access token from storage
 * @returns The access token if available and valid, null otherwise
 */
export const getAccessToken = (): string | null => {
    // Try to get from cookie first, then localStorage
    const token = Cookies.get("accessToken") || localStorage.getItem("accessToken");
    if (!token) return null;
    // Verify token validity
    if (isTokenValid()) return token;
    // Token is invalid or expired, clear tokens
    clearTokens();
    return null;
};

/**
 * Get the refresh token from storage
 * @returns The refresh token if available
 */
export const getRefreshToken = (): string | null => {
    return Cookies.get("refreshToken") || localStorage.getItem("refreshToken");
};

/**
 * Decode and return user info from the token
 * @returns User information from the token or null if invalid
 */
export const getUserFromToken = (): { id: string; email: string } | null => {
    try {
        const token = getAccessToken();
        if (!token) return null;
        const decoded = jwtDecode<JwtPayload>(token);
        return {
            id: decoded.sub,
            email: decoded.email,
        };
    } catch (error) {
        console.error("Error decoding token:", error);
        return null;
    }
};

/**
 * Attempts to restore authentication from stored tokens
 * @param dispatch Redux dispatch function
 * @returns Promise resolving to true if authentication was restored, false otherwise
 */
export const restoreAuth = async (dispatch: any): Promise<boolean> => {
    if (isTokenValid()) {
        const token = getAccessToken();

        if (token) {
            try {
                // Import these dynamically to avoid circular dependencies
                const { loginAsync, fetchUserProfile } = await import("../store/slices/authSlice");
                const { fetchFullProfile } = await import("../store/slices/profileSlice");
                // Restore authentication state
                await dispatch(loginAsync(token));
                await dispatch(fetchUserProfile(token));
                dispatch(fetchFullProfile(token));
                return true;
            } catch (error) {
                console.error("Failed to restore authentication:", error);
                return false;
            }
        }
    }

    return false;
};
